import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Unauthorized from '../pages/Unauthorized';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import { logger } from '../../utils/logger';
import LoadingScreen from '../common/LoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerification?: boolean;
  requireAdmin?: boolean;
}

/**
 * ProtectedRoute component that restricts access to authenticated users only
 * Optionally can require email verification
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireEmailVerification = false,
  requireAdmin = false,
}) => {  const { currentUser, loading, isEmailVerified, hasUsername, profileLoading } = useAuth();
  const { user } = useAppContext();
  const location = useLocation();
  const isChooseUsernamePage = location.pathname === '/choose-username';
  const isProfilePage = location.pathname === '/my-profile';

  logger.debug('🛂 ProtectedRoute: User from AppContext', user);
  logger.debug('🛂 ProtectedRoute: AuthContext loading', loading);
  logger.debug('🛂 ProtectedRoute: AuthContext profile loading', profileLoading);
  logger.debug('🛂 ProtectedRoute: AuthContext currentUser', currentUser);
  logger.debug('🛂 ProtectedRoute: AuthContext hasUsername', hasUsername);
  
  // Wait for auth and profile to resolve initially
  if (loading || profileLoading) {
    logger.debug('⏳ ProtectedRoute: AuthContext is loading or profileLoading. Waiting...');
    return <LoadingScreen message="Loading your profile..." />;
  }

  // If auth is resolved (loading is false) but no currentUser, then redirect
  if (!currentUser) {
    logger.info('🔒 Access to protected route denied - not authenticated (AuthContext loaded)');
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }
  // If admin is required, and we are still waiting for user context (which contains isAdmin)
  if (requireAdmin && user === null) {
    logger.debug('⏳ ProtectedRoute: requireAdmin is true and AppContext user is null. Waiting for admin status...');
    return <LoadingScreen message="Verifying permissions..." />;
  }

  // If email verification is required and the user's email is not verified
  if (requireEmailVerification && !isEmailVerified) {
    logger.info('🔒 Access to protected route denied - email not verified');
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }
  // If admin is required and user is not admin, redirect to home
  if (requireAdmin && !user?.isAdmin) {
    logger.info('🔒 Access to admin route denied - not authorized as admin');
    return <Unauthorized />;
  }
  
  // Special handling for username setup flow
  if (currentUser) {
    // If trying to access profile page but doesn't have username, redirect to choose username
    if (isProfilePage && !hasUsername) {
      logger.info('🔄 Redirecting from profile to choose-username (no username set)');
      return <Navigate to="/choose-username" replace />;
    }
    
    // If trying to access choose-username page but already has username, redirect to profile
    if (isChooseUsernamePage && hasUsername) {
      logger.info('🔄 Redirecting from choose-username to profile (username already set)');
      return <Navigate to="/my-profile" replace />;
    }
  }

  logger.info('🔓 Protected route access granted');
  return <>{children}</>;
};

export default ProtectedRoute;
